import { Badges } from '@components'
import { WroomImage } from '@components/wroom-image'
import { useElementSize } from '@haus-storefront-react/common-hooks/use-element-size'
import { useProduct } from '@haus-storefront-react/hooks'
import { ProductImageCarousel } from '@haus-storefront-react/product-image-carousel'
import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'
import { type SyntheticEvent } from 'react'
import { useFallbackImage } from '../../hooks/useFallbackImage'
import { getVendureProductId } from '../../utilities/utilities'
import styles from './ImageCarousel.module.scss'
import { ThumbnailStrip } from './ThumbnailStrip'

const DEFAULT_MAX_VISIBLE_THUMBNAILS = 5

interface ImageCarouselComponentProps {
  featuredAssetSource?: string
  onImageClick?: (activeSlide: number) => void
  activeSlide?: number
  onActiveSlideChange?: (activeSlide: number) => void
  useMeasuredWidth?: boolean
  showArrows?: boolean
  showBadges?: boolean
  maxVisibleThumbnails?: number
}

function removePhpPreloadImage(imgElement: HTMLImageElement): void {
  const root = imgElement.getRootNode()
  if (root instanceof ShadowRoot) {
    const host = root.host as HTMLElement
    const shell = host.parentElement
    const phpPreload = shell?.querySelector('[data-image-carousel-php-preload]')
    phpPreload?.remove()
    shell?.classList.add('image-carousel-react-ready')
  }
}

const ImageCarouselComponent = ({
  featuredAssetSource,
  onImageClick,
  activeSlide,
  onActiveSlideChange,
  useMeasuredWidth = true,
  showArrows = false,
  showBadges = true,
  maxVisibleThumbnails = DEFAULT_MAX_VISIBLE_THUMBNAILS,
}: ImageCarouselComponentProps) => {
  const { t } = useTranslation()
  const [imageRef, { width }] = useElementSize()
  const fallbackImage = useFallbackImage()
  const vendureProductId = getVendureProductId()
  const { data: product } = useProduct({ id: vendureProductId }, !!vendureProductId)
  if (!vendureProductId) {
    return null
  }

  const handleMainImageLoad = featuredAssetSource
    ? (e: SyntheticEvent<HTMLImageElement>) => {
        removePhpPreloadImage(e.currentTarget)
      }
    : undefined

  const isClickable = Boolean(onImageClick)

  return (
    <ProductImageCarousel.Root id={vendureProductId} preloadedAssetSrc={featuredAssetSource}>
      {({
        isLoading,
        assets,
        activeSlide: internalActiveSlide,
        setActiveSlide: internalSetActiveSlide,
      }) => {
        const currentActiveSlide =
          typeof activeSlide === 'number' ? activeSlide : internalActiveSlide
        const handleSetActiveSlide =
          onActiveSlideChange ??
          ((nextSlide: number) => {
            internalSetActiveSlide(nextSlide)
          })

        const hasMultipleAssets = assets.length > 1

        const goToPrev = () => {
          if (!hasMultipleAssets) return
          const nextIndex = currentActiveSlide === 0 ? assets.length - 1 : currentActiveSlide - 1
          handleSetActiveSlide(nextIndex)
        }

        const goToNext = () => {
          if (!hasMultipleAssets) return
          const nextIndex = (currentActiveSlide + 1) % assets.length
          handleSetActiveSlide(nextIndex)
        }

        const mainImage =
          assets.length > 0 && assets[currentActiveSlide]?.source ? (
            <WroomImage
              src={assets[currentActiveSlide].source}
              alt={
                assets[currentActiveSlide]?.name ||
                t('a11y.imageCarousel.aria.productImage', {
                  imageIndex: currentActiveSlide + 1,
                })
              }
              width={useMeasuredWidth ? width : undefined}
              ratio="1:1"
              priority={currentActiveSlide === 0}
              onLoad={handleMainImageLoad}
            />
          ) : !isLoading ? (
            <WroomImage
              width={useMeasuredWidth ? width : undefined}
              ratio="1:1"
              src={fallbackImage}
              onLoad={handleMainImageLoad}
              className={styles.carouselPlaceholder}
              alt={t('a11y.imageCarousel.aria.notAvailable')}
            />
          ) : null

        const mainImageWithBadges = (
          <div className={styles.productCarouselMainImageWrapper}>
            {mainImage}
            {showBadges && product && <Badges product={product} />}
          </div>
        )

        return (
          <ProductImageCarousel.Wrapper
            className={styles.imageCarouselWrapper}
            role="region"
            aria-label={t('a11y.imageCarousel.aria.carousel')}
          >
            <ProductImageCarousel.Carousel className={styles.productCarousel}>
              <div className={styles.productCarouselImageAreaContainer}>
                {showArrows && (
                  <button
                    type="button"
                    className={styles.overlayPrevButton}
                    onClick={goToPrev}
                    aria-label={t('a11y.imageCarousel.aria.previousImage')}
                    disabled={!hasMultipleAssets}
                  >
                    <ArrowLeftIcon width={32} height={32} aria-hidden />
                  </button>
                )}
                <ProductImageCarousel.InnerWrapper
                  ref={imageRef}
                  className={styles.productCarouselInner}
                >
                  <div className={styles.productCarouselImageArea}>
                    {isClickable ? (
                      <button
                        type="button"
                        className={styles.imageOverlayTrigger}
                        onClick={() => onImageClick?.(currentActiveSlide)}
                        aria-label={t('a11y.imageCarousel.aria.openGalleryLarge')}
                      >
                        {mainImageWithBadges}
                      </button>
                    ) : (
                      mainImageWithBadges
                    )}
                  </div>
                </ProductImageCarousel.InnerWrapper>

                {showArrows && (
                  <button
                    type="button"
                    className={styles.overlayNextButton}
                    onClick={goToNext}
                    aria-label={t('a11y.imageCarousel.aria.nextImage')}
                    disabled={!hasMultipleAssets}
                  >
                    <ArrowRightIcon width={32} height={32} aria-hidden />
                  </button>
                )}
              </div>
              <ThumbnailStrip
                assets={assets}
                currentActiveSlide={currentActiveSlide}
                handleSetActiveSlide={handleSetActiveSlide}
                maxVisibleThumbnails={maxVisibleThumbnails}
              />
            </ProductImageCarousel.Carousel>
          </ProductImageCarousel.Wrapper>
        )
      }}
    </ProductImageCarousel.Root>
  )
}

export default ImageCarouselComponent
